import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play, Plus, ThumbsUp, ThumbsDown, ChevronDown, Volume2, VolumeX, Check } from 'lucide-react';
import axios from 'axios';
import { getActiveProfileId } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAYA_LOGO = 'https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png';
const isOriginal = (it) => (it?.categories || []).includes('originals') || it?.category === 'originals';

let top10Promise = null;
function getTop10Ids() {
  if (!top10Promise) {
    let kids = false;
    try {
      const p = JSON.parse(sessionStorage.getItem('activeProfile') || 'null');
      kids = ['Tout public', '7+', '10+'].includes(p?.max_age_rating);
    } catch { /* ignore */ }
    top10Promise = axios.get(`${API}/top10${kids ? '?kids=true' : ''}`)
      .then(r => new Set([...(r.data?.movies || []), ...(r.data?.series || [])].map(c => c.id)))
      .catch(() => new Set());
  }
  return top10Promise;
}

/* "Nouvel épisode" badges: cached per profile, refreshed every 60s */
let epBadgeCache = { key: null, promise: null, time: 0 };
function getNewEpisodeBadges() {
  const pid = getActiveProfileId() || '';
  const now = Date.now();
  if (!epBadgeCache.promise || epBadgeCache.key !== pid || now - epBadgeCache.time > 60000) {
    epBadgeCache = {
      key: pid, time: now,
      promise: axios.get(`${API}/new-episode-badges`, { params: { profile_id: pid }, withCredentials: true })
        .then(r => new Set(r.data?.content_ids || []))
        .catch(() => new Set()),
    };
  }
  return epBadgeCache.promise;
}

function useNewEpisodeBadge(item) {
  const [has, setHas] = useState(false);
  useEffect(() => {
    if (item?.type !== 'series') return;
    let on = true;
    getNewEpisodeBadges().then(s => { if (on) setHas(s.has(item.id)); });
    return () => { on = false; };
  }, [item?.id, item?.type]);
  return has;
}

/* Netflix-style tile badges: Maya+ logo for Originals, orange TOP 10 tag, orange "Nouveaux épisodes", violet "Ajout récent" (1 tag max) */
const RECENT_MS = 15 * 86400000;
export function isRecentlyAdded(item) {
  if (!item) return false;
  const t = Date.parse(item.published_at || item.created_at || '');
  return !isNaN(t) && (Date.now() - t) < RECENT_MS;
}

export function TileBadges({ item, top10 = false, portrait = false }) {
  const newEpisode = useNewEpisodeBadge(item);
  const recent = !newEpisode && isRecentlyAdded(item);
  return (
    <>
      {newEpisode && (
        <span data-testid={`new-episode-badge-${item.id}`}
          className={`absolute bottom-1.5 left-1/2 -translate-x-1/2 z-20 bg-[#E9641A] text-white rounded-sm leading-none shadow-md pointer-events-none whitespace-nowrap ${portrait ? 'px-2 py-[4px]' : 'px-1.5 py-[3px]'}`}
          style={{ fontSize: portrait ? 13.5 : 9, fontWeight: 800, letterSpacing: '0.4px' }}>
          NOUVEAUX ÉPISODES
        </span>
      )}
      {recent && (
        <span data-testid={`recent-badge-${item.id}`}
          className={`absolute bottom-1.5 left-1/2 -translate-x-1/2 z-20 bg-[#7C3AED] text-white rounded-sm leading-none shadow-md pointer-events-none whitespace-nowrap ${portrait ? 'px-2 py-[4px]' : 'px-1.5 py-[3px]'}`}
          style={{ fontSize: portrait ? 13.5 : 9, fontWeight: 800, letterSpacing: '0.4px' }}>
          AJOUT RÉCENT
        </span>
      )}
      {isOriginal(item) && (
        <img src={MAYA_LOGO} alt="Maya+" data-testid={`original-badge-${item.id}`}
          className={`absolute z-20 h-3 w-auto pointer-events-none drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)] ${portrait ? 'top-1.5 left-1/2 -translate-x-1/2' : 'top-1.5 right-1.5'}`} />
      )}
      {top10 && (
        <span data-testid={`top10-badge-${item.id}`}
          className="absolute top-0 left-1.5 z-20 flex flex-col items-center bg-[#E9641A] text-white rounded-b px-[5px] pt-[3px] pb-[4px] leading-none shadow-md pointer-events-none">
          <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: '0.5px' }}>TOP</span>
          <span style={{ fontSize: 12, fontWeight: 900, marginTop: 1 }}>10</span>
        </span>
      )}
    </>
  );
}

function isAtLeast60Visible(rect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (rect.width === 0 || rect.height === 0) return false;
  const visW = Math.max(0, Math.min(vw, rect.right) - Math.max(0, rect.left));
  const visH = Math.max(0, Math.min(vh, rect.bottom) - Math.max(0, rect.top));
  return (visW * visH) / (rect.width * rect.height) >= 0.6;
}

function HoverTooltip({ text, visible }) {
  if (!visible || !text) return null;
  return (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 pointer-events-none z-50">
      <div className="bg-white text-black text-xs font-bold px-3 py-1.5 rounded whitespace-nowrap shadow-lg">
        {text}
      </div>
      <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white mx-auto" />
    </div>
  );
}

/* Netflix-style rating popup: 3 options inline at same level */
function RatingPopup({ rating, onRate }) {
  const [hovered, setHovered] = useState(null);

  const options = [
    { id: 'dislike', icon: ThumbsDown, label: 'Pas pour moi', size: 18, double: false },
    { id: 'like', icon: ThumbsUp, label: 'J\'aime bien ce titre', size: 18, double: false },
    { id: 'love', icon: ThumbsUp, label: 'J\'adore !', size: 16, double: true },
  ];

  return (
    <div className="flex items-center">
      {/* Tooltip above the hovered option */}
      {hovered && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none z-50">
          <div className="bg-white text-black text-xs font-bold px-3 py-1.5 rounded whitespace-nowrap shadow-lg">{options.find(o => o.id === hovered)?.label}</div>
          <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white mx-auto" />
        </div>
      )}
      <div className="flex items-center gap-0.5 bg-[#2a2a2a] rounded-full px-1.5 py-1 shadow-xl border border-white/10">
        {options.map(opt => {
          const Icon = opt.icon;
          const isActive = rating === opt.id;
          return (
            <button key={opt.id}
              onMouseEnter={() => setHovered(opt.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={(e) => { e.stopPropagation(); onRate(isActive ? null : opt.id); }}
              className={`flex items-center justify-center rounded-full transition-all duration-200 w-10 h-10
                ${isActive ? 'text-white scale-110' : 'text-white/70 hover:text-white hover:scale-110'}
                ${hovered === opt.id ? 'bg-white/15' : ''}
              `}
              style={isActive ? { animation: 'rate-pop 0.35s ease-out' } : undefined}
            >
              {opt.double ? (
                <span className="flex items-center -space-x-1.5">
                  <Icon size={opt.size} fill={isActive ? '#E9641A' : 'none'} className={isActive ? 'text-[#E9641A]' : ''} />
                  <Icon size={opt.size} fill={isActive ? '#E9641A' : 'none'} className={isActive ? 'text-[#E9641A]' : ''} />
                </span>
              ) : (
                <Icon size={opt.size} fill={isActive ? '#E9641A' : 'none'} className={isActive ? 'text-[#E9641A]' : ''} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HoverCard({ item, onClose, onNavigate, onLaunchVideo, style, onListChanged }) {
  const videoRef = useRef(null);
  const cardRef = useRef(null);
  const [show, setShow] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);
  const [trailerMuted, setTrailerMuted] = useState(true);
  const [inList, setInList] = useState(false);
  const [listAnim, setListAnim] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(null);
  const [ratingAnim, setRatingAnim] = useState(false);
  const hasTrailer = item.type !== 'game' && !!item.trailer_url;
  const isSeries = item.type === 'series';
  const [isTop10Item, setIsTop10Item] = useState(false);
  useEffect(() => {
    let ok = true;
    getTop10Ids().then(s => { if (ok) setIsTop10Item(s.has(item.id)); });
    return () => { ok = false; };
  }, [item.id]);

  // Check if item is in watchlist + existing rating
  useEffect(() => {
    axios.get(`${API}/watchlist`, { params: { profile_id: getActiveProfileId() }, withCredentials: true })
      .then(r => { if (r.data?.some(c => c.id === item.id)) setInList(true); })
      .catch(() => {});
    axios.get(`${API}/ratings`, { withCredentials: true })
      .then(r => { if (r.data?.[item.id]) setRating(r.data[item.id]); })
      .catch(() => {});
  }, [item.id]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    el.getBoundingClientRect();
    const raf = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (!hasTrailer) return;
    const timer = setTimeout(() => setShowTrailer(true), 1000);
    return () => clearTimeout(timer);
  }, [hasTrailer]);

  const toggleList = async (e) => {
    e.stopPropagation();
    try {
      if (inList) {
        await axios.delete(`${API}/watchlist/${item.id}`, { params: { profile_id: getActiveProfileId() }, withCredentials: true });
        setInList(false);
        if (onListChanged) onListChanged(item.id, false);
      } else {
        await axios.post(`${API}/watchlist/${item.id}`, { profile_id: getActiveProfileId() }, { withCredentials: true });
        setInList(true);
        setListAnim(true);
        setTimeout(() => setListAnim(false), 400);
        if (onListChanged) onListChanged(item.id, true);
      }
    } catch {}
  };

  const handleRate = (r) => {
    setRating(r);
    if (r) { setRatingAnim(true); setTimeout(() => setRatingAnim(false), 400); }
    setShowRating(false);
    axios.post(`${API}/ratings`, { content_id: item.id, rating: r }, { withCredentials: true }).catch(() => {});
  };

  const handlePlayClick = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (v) { v.pause(); v.muted = true; v.removeAttribute('src'); v.load(); }
    if (item.type !== 'game' && onLaunchVideo) onLaunchVideo(item.id);
  };

  const btnBase = "relative rounded-full border-2 flex items-center justify-center transition-all duration-200";
  const btnSize = "w-11 h-11";

  return (
    <div
      ref={cardRef}
      className="absolute z-50 bg-[#181818] rounded-lg shadow-2xl shadow-black/90 overflow-visible"
      style={{
        ...style, width: '430px', transformOrigin: 'center center',
        transition: show ? 'opacity 150ms ease-out, transform 150ms ease-out, filter 150ms ease-out' : 'none',
        opacity: show ? 1 : 0, transform: show ? 'scale(1)' : 'scale(0.78)', filter: show ? 'blur(0px)' : 'blur(10px)',
      }}
      onMouseLeave={() => { setShowRating(false); onClose(); }}
      data-testid={`hover-card-${item.id}`}
    >
      <style>{`
        @keyframes rate-pop { 0%{transform:scale(1)} 30%{transform:scale(1.35)} 60%{transform:scale(0.9)} 100%{transform:scale(1.1)} }
        @keyframes list-check { 0%{transform:scale(0.5) rotate(-20deg);opacity:0} 50%{transform:scale(1.2) rotate(5deg);opacity:1} 100%{transform:scale(1) rotate(0deg);opacity:1} }
      `}</style>

      {/* Preview */}
      <div className="relative aspect-video bg-black cursor-pointer rounded-t-lg overflow-hidden" onClick={handlePlayClick}>
        <img src={item.banner_url || item.thumbnail_url} alt={item.title}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: showTrailer && hasTrailer ? 0 : 1, zIndex: 2 }} />
        {hasTrailer && (
          <video ref={videoRef} src={item.trailer_url}
            className="w-full h-full object-cover transition-opacity duration-700"
            style={{ opacity: showTrailer ? 1 : 0 }}
            muted={trailerMuted} autoPlay loop playsInline
            onError={(e) => { e.target.style.display = 'none'; }} />
        )}
        <TileBadges item={item} top10={isTop10Item} />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#181818] to-transparent h-20 z-10" />
        <div className="absolute bottom-3 left-4 z-10">
          {item.logo_url ? (
            <img src={item.logo_url} alt={item.title} className="h-8 w-auto drop-shadow-lg transition-opacity duration-700" style={{ opacity: showTrailer ? 1 : 0 }} />
          ) : (
            <p className="text-white font-bold text-base drop-shadow-lg">{item.title}</p>
          )}
        </div>
        {hasTrailer && showTrailer && (
          <button data-testid={`hover-mute-${item.id}`}
            onClick={(e) => { e.stopPropagation(); setTrailerMuted(!trailerMuted); }}
            className="absolute bottom-3 right-3 z-20 w-9 h-9 rounded-full border border-white/40 flex items-center justify-center text-white/60 hover:text-white hover:border-white bg-black/50 backdrop-blur-sm transition-all">
            {trailerMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        )}
      </div>

      {/* Info */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {/* Play */}
            {item.type === 'game' ? (
              <button data-testid={`hover-play-${item.id}`}
                onClick={e => { e.stopPropagation(); onNavigate(`/game/${item.id}`); }}
                className="w-11 h-11 rounded-full bg-white flex items-center justify-center hover:bg-white/80 transition-all duration-200 hover:scale-110">
                <Play size={22} fill="black" className="text-black ml-0.5" />
              </button>
            ) : (
              <div className="relative" onMouseEnter={() => setHoveredBtn('play')} onMouseLeave={() => setHoveredBtn(null)}>
                <HoverTooltip text="Lecture" visible={hoveredBtn === 'play' && !showRating} />
                <button data-testid={`hover-play-${item.id}`} onClick={handlePlayClick}
                  className="w-11 h-11 rounded-full bg-white flex items-center justify-center hover:bg-white/80 transition-all duration-200 hover:scale-110">
                  <Play size={22} fill="black" className="text-black ml-0.5" />
                </button>
              </div>
            )}

            {/* Add/Remove list */}
            <div className="relative" onMouseEnter={() => setHoveredBtn('list')} onMouseLeave={() => setHoveredBtn(null)}>
              <HoverTooltip text={inList ? 'Supprimer de Ma liste' : 'Ajouter à Ma liste'} visible={hoveredBtn === 'list' && !showRating} />
              <button data-testid={`hover-add-${item.id}`} onClick={toggleList}
                className={`${btnBase} ${btnSize} ${
                  inList ? 'border-white/60 text-white' : 'border-white/40 text-white/70 hover:border-white hover:text-white'
                } hover:scale-110`}>
                {inList ? (
                  <Check size={20} strokeWidth={3} style={listAnim ? { animation: 'list-check 0.4s ease-out' } : undefined} />
                ) : (
                  <Plus size={22} />
                )}
              </button>
            </div>

            {/* Rating button with 3-choice popup — inline at same level */}
            <div className="relative"
              onMouseEnter={() => { setHoveredBtn('like'); setShowRating(true); }}
              onMouseLeave={() => { setHoveredBtn(null); setShowRating(false); }}>
              {showRating ? (
                <RatingPopup rating={rating} onRate={handleRate} />
              ) : (
                <>
                  <HoverTooltip text={rating === 'like' ? 'Noté : j\'aime' : rating === 'love' ? 'Noté : j\'adore' : rating === 'dislike' ? 'Noté : pas pour moi' : 'J\'aime bien ce titre'} visible={hoveredBtn === 'like'} />
                  <button data-testid={`hover-like-${item.id}`}
                    className={`${btnBase} ${btnSize} ${
                      rating ? 'border-white/60 text-[#E9641A]' : 'border-white/40 text-white/70 hover:border-white hover:text-white'
                    } hover:scale-110`}
                    style={ratingAnim ? { animation: 'rate-pop 0.35s ease-out' } : undefined}>
                    {rating === 'dislike' ? (
                      <ThumbsDown size={19} fill="#E9641A" />
                    ) : rating === 'love' ? (
                      <span className="flex items-center -space-x-1.5">
                        <ThumbsUp size={16} fill="#E9641A" className="text-[#E9641A]" />
                        <ThumbsUp size={16} fill="#E9641A" className="text-[#E9641A]" />
                      </span>
                    ) : rating === 'like' ? (
                      <ThumbsUp size={19} fill="#E9641A" />
                    ) : (
                      <ThumbsUp size={19} />
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Info / Episodes */}
          <div className="relative" onMouseEnter={() => setHoveredBtn('info')} onMouseLeave={() => setHoveredBtn(null)}>
            <HoverTooltip text={isSeries ? 'Épisodes et infos' : 'Plus d\'infos'} visible={hoveredBtn === 'info' && !showRating} />
            <button data-testid={`hover-info-${item.id}`}
              onClick={(e) => {
                e.stopPropagation();
                const rect = cardRef.current?.getBoundingClientRect();
                onNavigate(`/content/${item.id}`, true, rect ? { left: rect.left, top: rect.top, width: rect.width, height: rect.height } : null);
              }}
              className={`${btnBase} ${btnSize} border-white/40 text-white/70 hover:border-white hover:text-white hover:scale-110`}>
              <ChevronDown size={22} />
            </button>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-2 text-sm mb-2">
          {item.age_rating && <span className="px-1.5 py-0.5 border border-white/30 rounded text-white/70 text-xs">{item.age_rating}</span>}
          {item.duration && <span className="text-white/50">{item.duration}</span>}
          <span className="px-1.5 py-0.5 border border-white/30 rounded text-white/70 text-xs font-semibold">{item.rating || 'HD'}</span>
          {item.content_filters?.length > 0 && (
            <span className="px-1.5 py-0.5 border border-[#E9641A]/40 rounded text-[#E9641A] text-xs flex items-center gap-1">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-sm text-white/50">
          {item.tags?.slice(0, 3).map((tag, i) => (
            <React.Fragment key={tag}>
              {i > 0 && <span className="text-white/20">&middot;</span>}
              <span className="capitalize">{tag}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Wrapper that handles close animation */
function HoverCardWrapper({ item, onClose, onNavigate, onLaunchVideo, style, onRemoved, onListChanged }) {
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onRemoved(), 130);
  }, [onRemoved]);

  return (
    <div
      style={{
        transition: closing ? 'opacity 150ms ease-in, transform 150ms ease-in, filter 150ms ease-in' : undefined,
        opacity: closing ? 0 : undefined,
        transform: closing ? 'scale(0.78)' : undefined,
        filter: closing ? 'blur(10px)' : undefined,
        position: 'fixed', left: 0, top: 0, width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 9999,
      }}
    >
      <div style={{ pointerEvents: closing ? 'none' : 'auto' }}>
        <HoverCard
          item={item}
          onClose={handleClose}
          onNavigate={(path, isModal, sourceRect) => {
            handleClose();
            if (isModal && onClose) {
              onClose(item.id, sourceRect);
            } else if (!isModal && onNavigate) {
              onNavigate(path);
            }
          }}
          onLaunchVideo={(id) => {
            handleClose();
            if (onLaunchVideo) onLaunchVideo(id);
          }}
          onListChanged={onListChanged}
          style={style}
        />
      </div>
    </div>
  );
}

/* Featured Banner (Highlight) — static thumbnail only, no trailer */
function FeatureBanner({ item, onOpenModal }) {
  const navigate = useNavigate();
  if (!item) return null;
  return (
    <div
      className="relative w-screen overflow-hidden cursor-pointer group"
      style={{ height: '360px', marginLeft: 'calc(-1 * (50vw - 50%))', width: '100vw' }}
      onClick={() => onOpenModal(item.id)}
      data-testid={`feature-banner-${item.id}`}
    >
      <img src={item.banner_url || item.thumbnail_url} alt={item.title}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-all duration-700" />
      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#11041F] to-transparent z-10" />
      <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#11041F] to-transparent z-10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#11041F] via-[#11041F]/20 to-transparent z-10" />
      <div className="absolute bottom-0 left-0 p-8 md:p-12 z-20">
        {item.logo_url ? (
          <img src={item.logo_url} alt={item.title} className="max-h-16 md:max-h-24 w-auto mb-2 drop-shadow-xl" />
        ) : (
          <h3 className="text-2xl md:text-4xl font-extrabold text-white mb-2">{item.title}</h3>
        )}
        <p className="text-sm text-white/60 max-w-lg line-clamp-2 mb-4">{item.description}</p>
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (item.type === 'game') { navigate(`/game/${item.id}`); return; }
              window.dispatchEvent(new CustomEvent('launchMedia', { detail: item.id }));
            }}
            className="flex items-center gap-2 bg-[#75D4D5] text-white font-bold px-6 py-2.5 rounded-md hover:bg-[#75D4D5]/80 transition-all text-sm"
            data-testid={`feature-play-${item.id}`}>
            <Play size={16} fill="white" /> <span>Lecture</span>
          </button>
          <button onClick={(e) => { e.stopPropagation(); onOpenModal(item.id); }} data-testid={`feature-info-${item.id}`} className="flex items-center gap-2 bg-white/15 text-white font-semibold px-6 py-2.5 rounded-md backdrop-blur-md hover:bg-white/25 transition-all text-sm">
            Plus d'infos
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ContentRow({ title, items = [], isTop10 = false, onOpenModal, showProgress = false, onLaunch, launching = false, onListChanged }) {
  const scrollRef = useRef(null);
  const rowRef = useRef(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const hoverTimer = useRef(null);
  const currentHoverId = useRef(null);
  const [hoverPos, setHoverPos] = useState({ left: 0, top: 0 });
  const navigate = useNavigate();
  const [top10Ids, setTop10Ids] = useState(null);
  useEffect(() => {
    let ok = true;
    getTop10Ids().then(s => { if (ok) setTop10Ids(s); });
    return () => { ok = false; };
  }, []);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);
  const velRef = useRef(0);
  const lastMoveRef = useRef({ x: 0, t: 0 });
  const momentumRaf = useRef(null);

  const handleDragMove = useCallback((e) => {
    if (!isDragging.current || !scrollRef.current) return;
    const delta = dragStartX.current - e.clientX;
    if (Math.abs(delta) > 5) hasDragged.current = true;
    scrollRef.current.scrollLeft = scrollStartX.current + delta;
    const now = performance.now();
    const dt = now - lastMoveRef.current.t;
    if (dt > 0) velRef.current = (lastMoveRef.current.x - e.clientX) / dt;
    lastMoveRef.current = { x: e.clientX, t: now };
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
    const el = scrollRef.current;
    if (!el) return;
    el.style.cursor = 'grab';
    // Momentum: keep sliding after release, decelerating
    let v = velRef.current;
    if (hasDragged.current && Math.abs(v) > 0.15) {
      const step = () => {
        const s = scrollRef.current;
        if (!s || isDragging.current) return;
        s.scrollLeft += v * 16;
        v *= 0.94;
        if (Math.abs(v) > 0.04) momentumRaf.current = requestAnimationFrame(step);
        else s.style.scrollBehavior = '';
      };
      momentumRaf.current = requestAnimationFrame(step);
    } else {
      el.style.scrollBehavior = '';
    }
    // Keep hasDragged briefly true so the click right after a drag is ignored
    setTimeout(() => { hasDragged.current = false; }, 50);
  }, [handleDragMove]);

  const handleDragStart = (e) => {
    if (e.button !== 0 || !scrollRef.current) return;
    cancelAnimationFrame(momentumRaf.current);
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    scrollStartX.current = scrollRef.current.scrollLeft;
    velRef.current = 0;
    lastMoveRef.current = { x: e.clientX, t: performance.now() };
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.scrollBehavior = 'auto';
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
  };

  useEffect(() => () => {
    cancelAnimationFrame(momentumRaf.current);
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
  }, [handleDragMove, handleDragEnd]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // Boucle : flèche droite en fin de ligne → retour au début
    if (dir === 'right' && el.scrollLeft + el.clientWidth >= el.scrollWidth - 10) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    el.scrollBy({ left: dir === 'left' ? -el.clientWidth * 0.75 : el.clientWidth * 0.75, behavior: 'smooth' });
  };

  const handleMouseEnter = (item, e) => {
    if (isDragging.current || launching) return;
    const rect = e.currentTarget.getBoundingClientRect();
    clearTimeout(hoverTimer.current);
    if (!isAtLeast60Visible(rect)) return;

    currentHoverId.current = item.id;
    hoverTimer.current = setTimeout(() => {
      if (currentHoverId.current !== item.id) return;
      const cardW = 430;
      const cardH = 320;
      const margin = 12;
      let left = rect.left + rect.width / 2 - cardW / 2;
      let top = rect.top + rect.height / 2 - cardH / 2;
      if (left < margin) left = margin;
      if (left + cardW > window.innerWidth - margin) left = window.innerWidth - margin - cardW;
      if (top < margin) top = margin;
      if (top + cardH > window.innerHeight - margin) top = window.innerHeight - margin - cardH;
      setHoverPos({ left, top });
      setHoveredItem(item);
    }, 500);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    currentHoverId.current = null;
  };

  const handleRemoveHover = useCallback(() => {
    setHoveredItem(null);
  }, []);

  const handleLaunchFromHover = useCallback((contentId) => {
    setHoveredItem(null);
    if (onLaunch) onLaunch(contentId);
    else navigate(`/watch/${contentId}`);
  }, [onLaunch, navigate]);

  // Close on scroll
  useEffect(() => {
    const onScroll = () => {
      clearTimeout(hoverTimer.current);
      currentHoverId.current = null;
      setHoveredItem(null);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close hover when launching
  useEffect(() => {
    if (launching) setHoveredItem(null);
  }, [launching]);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  if (!items.length) return null;

  const thumbH = 280;

  return (
    <div className="relative group/row mb-10" ref={rowRef} data-testid={`content-row-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      {title && (
        <h2 className="text-xl sm:text-2xl font-bold font-['Manrope'] text-white mb-4 tracking-tight px-1">{title}</h2>
      )}
      <div className="relative">
        <button onClick={() => scroll('left')} data-testid={`row-scroll-left-${title}`}
          className="absolute left-0 top-0 bottom-0 z-30 w-12 bg-gradient-to-r from-[#11041F]/90 to-transparent flex items-center justify-center text-white/50 hover:text-white opacity-0 group-hover/row:opacity-100 transition-all duration-300">
          <ChevronLeft size={32} />
        </button>
        <button onClick={() => scroll('right')} data-testid={`row-scroll-right-${title}`}
          className="absolute right-0 top-0 bottom-0 z-30 w-12 bg-gradient-to-l from-[#11041F]/90 to-transparent flex items-center justify-center text-white/50 hover:text-white opacity-0 group-hover/row:opacity-100 transition-all duration-300">
          <ChevronRight size={32} />
        </button>

        <div ref={scrollRef} className={`flex ${isTop10 ? 'gap-2 md:gap-3' : 'gap-1.5 md:gap-2'} overflow-x-auto hide-scrollbar scroll-smooth content-row-scroll`} style={{ cursor: 'grab' }}
          onMouseDown={handleDragStart} onDragStart={(e) => e.preventDefault()}
          onWheel={(e) => {
            // Let vertical scroll pass through to the page, only handle horizontal
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;
            if (scrollRef.current) {
              e.preventDefault();
              scrollRef.current.scrollLeft += e.deltaX;
            }
          }}>
          {items.map((item, idx) => (
            <div key={item.id} data-testid={`content-card-${item.id}`} className="flex-none cursor-pointer group/card content-card-gpu"
              style={{
                width: isTop10 ? undefined : 'calc(17.55% - 6px)',
                minWidth: isTop10 ? undefined : '189px',
                transition: launching ? 'transform 1.6s ease-out, opacity 1.4s ease-out' : undefined,
                transform: launching ? 'scale(0.8)' : undefined,
                opacity: launching ? 0 : undefined,
                transitionDelay: launching ? `${idx * 30}ms` : undefined,
              }}
              onClick={() => {
                if (hasDragged.current) return;
                if (showProgress && item.type !== 'game' && onLaunch) {
                  onLaunch(item.id);
                } else if (onOpenModal) {
                  onOpenModal(item.id);
                } else {
                  navigate(`/content/${item.id}`);
                }
              }}
              onMouseEnter={(e) => handleMouseEnter(item, e)}
              onMouseLeave={handleMouseLeave}>
              {isTop10 ? (
                <div className="relative flex items-end" style={{ height: `${thumbH}px` }}>
                  <span className="font-black select-none" style={{
                    color: '#E9641A',
                    WebkitTextStroke: '3px rgba(255,255,255,0.25)',
                    paintOrder: 'stroke fill',
                    fontFamily: 'Manrope, sans-serif',
                    zIndex: 2, marginRight: '-35px', flexShrink: 0,
                    fontSize: `${thumbH * 1.1}px`,
                    lineHeight: `${thumbH * 0.85}px`,
                    display: 'block',
                    WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 35%, rgba(0,0,0,0) 100%)',
                    maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 35%, rgba(0,0,0,0) 100%)',
                  }}>
                    {idx + 1}
                  </span>
                  <div className="relative rounded-md overflow-hidden flex-none shadow-xl shadow-black/60" style={{ zIndex: 3, height: `${thumbH}px`, width: `${Math.round(thumbH * 2 / 3)}px` }}>
                    <img src={item.poster_url || item.thumbnail_url} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                    <TileBadges item={item} portrait />
                  </div>
                </div>
              ) : (
                <div className="relative aspect-video rounded-md overflow-hidden bg-[#1a0e2e] transition-all duration-300">
                  <img src={item.banner_url || item.thumbnail_url} alt={item.title} className="w-full h-full object-cover group-hover/card:brightness-110 transition-all duration-300" loading="lazy" decoding="async" />
                  <TileBadges item={item} top10={!!top10Ids?.has(item.id)} />
                  {showProgress && item.progress > 0 && (
                    <>
                      {item.remaining_label && (
                        <span className="absolute bottom-2 right-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-medium leading-none" data-testid={`remaining-time-${item.id}`}>
                          {item.remaining_label}
                        </span>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20" data-testid={`progress-bar-${item.id}`}>
                        <div className="h-full bg-[#E9641A] rounded-r-sm" style={{ width: `${item.progress}%` }} />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {hoveredItem && (
        <HoverCardWrapper
          key={hoveredItem.id}
          item={hoveredItem}
          onClose={(id, sourceRect) => { handleRemoveHover(); if (onOpenModal) onOpenModal(id, sourceRect); }}
          onNavigate={(path) => { handleRemoveHover(); navigate(path); }}
          onLaunchVideo={handleLaunchFromHover}
          onRemoved={handleRemoveHover}
          onListChanged={onListChanged}
          style={{ position: 'fixed', left: hoverPos.left, top: hoverPos.top }}
        />
      )}
    </div>
  );
}

export { FeatureBanner };
